<template>
  <vetster-slide-over
    ref="membershipSlideOver"
    container-classes="lg:h-[610px]"
    size="2xl"
  >
    <template #content>
      <div class="flex flex-col flex-1">
        <div class="flex flex-col items-center overflow-y-auto relative bg-ocean-950 text-white text-center px-4 py-6 lg:py-8 space-y-4 font-title">
          <vetster-slide-over-common-heading
            v-if="!$screen('lg')"
            :show-padding="false"
            class="self-start"
            @close="close"
          />
          <h1
            class="text-xl lg:text-3xl leading-6"
            v-html="$t('search.save-up-to')"
          />
          <p
            class="text-xs lg:text-xl lg:text-pretty"
            v-html="$t('search.subscribe-to-vetster-plus')"
          />
        </div>

        <div class="px-4 pb-3 pt-4 lg:pt-6 flex flex-col flex-1">
          <p class="text-neutral-900 text-2xl font-bold font-title">
            {{ $t('search.choose-a-payment') }}
          </p>

          <!-- Vetster Plus -->
          <div class="flex flex-col lg:grid lg:grid-cols-2 lg:gap-3 lg:mt-4">
            <div
              v-if="!loading"
              class="w-full lg:w-auto h-full rounded-lg mt-4 lg:mt-0 lg-h-auto outline"
              :class="{
                'outline-neutral-900 outline-1': !isMembershipSelected,
                'outline-bubblegum-500 outline-2': isMembershipSelected,
              }"
            >
              <button
                type="button"
                :aria-label="$t('search.purchase-vetster-plus')"
                class="flex shrink-0 rounded-lg border-transparent bg-white font-inter text-left w-full"
                @click="isMembershipSelected = true"
              >
                <div class="w-full px-4 pt-4 pb-3">
                  <div
                    v-if="isPromotionEnabled"
                    class="flex justify-between mb-1"
                  >
                    <vetster-plus-badge />
                    <div class="py-0.5 px-2 flex justify-between gap-x-1.5 items-center rounded-md bg-bubblegum-50">
                      <fa
                        :icon="['fal', 'clock']"
                        class="size-3.5 text-bubblegum"
                      />
                      <span class="text-xs text-neutral-900 font-semibold">
                        {{ $t('membership-subscriptions.limited-time-offer') }}
                      </span>
                    </div>
                  </div>
                  <div class="text-neutral-900 flex items-baseline justify-between font-semibold w-full">
                    <p>
                      {{ $t('search.vetster-plus') }}
                    </p>
                    <span>
                      <span
                        v-if="hasPromotion"
                        class="text-xs line-through inline-flex font-normal"
                      >
                        {{ formattedOriginalMonthlyAmount }}
                      </span>
                      {{ formattedMonthlyAmount }}<span class="font-normal text-sm">/{{ $t('membership-subscriptions.month') }}</span>
                    </span>
                  </div>
                  <p class="text-neutral-500 mb-3 text-xs lg:mt-2">
                    <template v-if="isPromotionEnabled">
                      {{
                        $t('membership-subscriptions.billed-today-and-renews-annually-at-cancel-anytime', {
                          price: formattedAnnualAmount,
                          renewPrice: formattedOriginalAnnualAmount,
                        })
                      }}
                    </template>
                    <template v-else>
                      <span
                        v-if="hasPromotion"
                        class="line-through"
                      >
                        {{ formattedOriginalAnnualAmount }}
                      </span>
                      {{ billingDetails }}
                    </template>
                  </p>

                  <!-- Benefits -->
                  <ul class="space-y-2">
                    <vetster-benefit-list-item
                      v-for="perk in getYearlySubscriptionPlan.perks"
                      :key="perk.uuid"
                      :is-new="perk.is_new"
                      :label="perk.name"
                    />
                  </ul>
                </div>
              </button>
            </div>

            <!-- Pay per appointment -->
            <div
              class="mt-4 flex shrink-0 rounded-lg outline bg-white font-inter text-left w-full lg:w-auto lg:mt-0 lg:flex-1"
              :class="{
                'outline-neutral-900 outline-1': isMembershipSelected,
                'outline-bubblegum-500 outline-2': !isMembershipSelected,
              }"
            >
              <button
                type="button"
                class="w-full"
                :aria-label="$t('search.pay-for-this-appointment')"
                @click="isMembershipSelected = false"
              >
                <div class="w-full h-full rounded-lg text-neutral-900">
                  <div class="w-full px-4 pt-4 pb-3">
                    <div class="flex items-baseline justify-between font-semibold w-full">
                      {{ $t('search.pay-for-this-appointment') }}
                      <span v-if="getQuoteTotal">
                        {{ $n(formatMoneyRounded(getQuoteTotal.amount), getCurrencyConfig(getQuoteTotal.currency)) }}
                      </span>
                    </div>
                    <ul
                      v-if="hasSubsPayGoPerks"
                      class="flex flex-col items-start text-start space-y-2 mt-2"
                    >
                      <li
                        v-for="perk in subsPayGoPerks"
                        :key="perk.uuid"
                      >
                        <vetster-benefit-list-item
                          :label="perk.name"
                          :is-crossed="!perk.is_included"
                        />
                      </li>
                    </ul>
                    <div
                      v-else
                      class="text-xs text-left text-neutral-500 lg:mt-2"
                    >
                      {{ $t('search.single-veterinary-appointment') }}
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </div>

          <div class="mt-4 px-1">
            <i18n
              path="search.estimate-savings-based-html"
              class="text-xs text-neutral-500"
              tag="p"
            >
              <template #relatedFees>
                <button
                  type="button"
                  class="underline underline-offset-2 font-medium hover:opacity-70"
                  @click.stop="toggleRelatedFeesModal"
                >
                  {{ $t('search.prescription-fees') }}
                </button>
              </template>
            </i18n>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-center lg:justify-end border-t p-4 lg:p-5">
        <vetster-button
          variant="ocean"
          class="w-full lg:w-auto text-sm"
          @click="checkout"
        >
          {{ buttonText }}
        </vetster-button>
      </div>
    </template>
  </vetster-slide-over>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDown,
  faCheck,
} from '@fortawesome/pro-solid-svg-icons';
import { getCurrentInstance } from 'vue';
import { mapGetters } from 'vuex';

import VetsterBenefitListItem from '@/brand-components/VetsterBenefitListItem';
import VetsterPlusBadge from '@/brand-components/VetsterPlusBadge';
import VetsterSlideOver from '@/brand-components/VetsterSlideOver';
import VetsterSlideOverCommonHeading from '@/brand-components/VetsterSlideOverCommonHeading';
import useCheckoutHelpers from '@/composables/use-checkout-helpers';
import {
  formatMoney,
  formatMoneyRounded,
} from '@/helpers/format';
import HasYearlySubscriptionPlan from '@/mixins/HasYearlySubscriptionPlan';
import ParseErrors from '@/mixins/ParseErrors';

library.add({
  faAngleDown,
  faCheck,
});

export default {
  name: 'VetsterMembershipSlideOver',

  components: {
    VetsterSlideOverCommonHeading,
    VetsterBenefitListItem,
    VetsterSlideOver,
    VetsterPlusBadge,
  },

  mixins: [
    HasYearlySubscriptionPlan,
    ParseErrors,
  ],

  setup() {
    const root = getCurrentInstance().proxy.$root;
    const { subsPayGoPerks } = useCheckoutHelpers(root);

    return { subsPayGoPerks };
  },

  data() {
    return {
      isMembershipSelected: false,
      loading: false,
    };
  },

  computed: {
    ...mapGetters('quote', ['getQuote']),
    ...mapGetters('subscriptionPlans', [
      'getYearlySubscriptionPlan',
      'hasPromotion',
    ]),
    ...mapGetters(['isEcommerceCapable']),
    billedAt() {
      return this.price ? this.price.amount / 100 : null;
    },
    billingDetails() {
      return this.$t('search.billed-today', { price: this.billedAt });
    },
    getQuoteTotal() {
      return this.getQuote?.total;
    },
    buttonText() {
      return this.isMembershipSelected ? this.$t('search.continue-with-vetster-plus') : this.$t('app.continue');
    },
    hasSubsPayGoPerks() {
      return this.subsPayGoPerks?.length > 0;
    },
  },

  created() {
    this.fetchSubscriptionPlan();
  },

  methods: {
    checkout() {
      this.$analytics.track({
        eventName: 'payment_option_continue',
        parent: 'guided-search',
        child: 'select_payment_option',
        type: this.plan.type,
        cycle: this.plan.interval_type,
        amount: this.billedAt,
        version: '2.0',
        windowLocation: window.location.href,
      });

      this.$emit('checkout', this.isMembershipSelected);
    },

    close() {
      this.$refs.membershipSlideOver.close();
    },

    async fetchSubscriptionPlan() {
      try {
        this.loading = true;
        await this.$store.dispatch('subscriptionPlans/fetchSubscriptionPlan');
        this.isMembershipSelected = true;
      } catch (e) {
        this.showError(e, true);
      } finally {
        this.loading = false;
      }
    },

    firePaymentOptionLoadEvents() {
      this.$analytics.track({
        eventName: 'cx:checkout:payment-option:load',
        version: '2.0',
      });

      this.$analytics.track({
        eventName: 'payment_option_load',
        parent: 'guided-search',
        child: 'select_payment_option',
        version: '2.0',
        windowLocation: window.location.href,
      });
    },

    formatMoney,
    formatMoneyRounded,

    getCurrencyConfig(currency) {
      return {
        currency: currency ?? this.price.currency,
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        style: 'currency',
      };
    },

    open() {
      this.firePaymentOptionLoadEvents();
      this.$refs.membershipSlideOver.open();
    },

    toggleRelatedFeesModal() {
      this.$emit('open-related-fees');
    },
  },
};
</script>
